import { inject, Injectable, OnDestroy } from '@angular/core';
import { MenuCommand } from '../shared/models/menu-command';
import {
  routeProjects,
  routeLogoff,
  routeSettings,
  routeManual,
  routeOrders,
  routeOfflineOrders
} from '../liveformsweb.routes';
import { RBCheck } from '@lfw/roesberg';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { SettingsService } from '../shared/settings/settings.service';
import { ProjectsService } from '../projects/projects.service';
import { OfflineFormsClient } from '../orders/offline-orders/offline-forms.client';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NetworkService } from '../network.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class MenuService implements OnDestroy {
  private subscription: Subscription = new Subscription();

  public rebuilt$ = new Subject<void>();

  public cmdProject?: MenuCommand;
  public cmdOrders?: MenuCommand;
  public cmdOfflineOrders?: MenuCommand;
  public cmdSearch?: MenuCommand;
  public cmdSettings?: MenuCommand;
  public cmdSignOff?: MenuCommand;
  public cmdUser?: MenuCommand;
  /*
  public cmdInfoChanged?: MenuCommand;
  public cmdInfoHistory?: MenuCommand;
  public cmdInfoWebDoku?: MenuCommand;
   */
  public cmdDefaultHelp?: MenuCommand;
  public cmdCustomHelp?: MenuCommand;

  public navigationAllowed = true;

  private projectService = inject(ProjectsService);
  private settingsService = inject(SettingsService);
  private offlineFormsIndexDbClient = inject(OfflineFormsClient);
  private translateService = inject(TranslateService);
  private networkService = inject(NetworkService);

  constructor() {
    this.buildCommands();
    this.subscription.add(
      this.projectService.projectChange.subscribe(() => {
        this.buildCommands();
      })
    );
    this.subscription.add(
      this.settingsService.settingsLoaded.subscribe(() => {
        this.buildCommands();
      })
    );
    this.offlineFormsIndexDbClient.offlineOrders$.pipe(takeUntilDestroyed()).subscribe(() => this.buildCommands());
    this.networkService.state$.pipe(takeUntilDestroyed()).subscribe(() => this.buildCommands());
  }

  // wird nicht aufgerufen !
  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public async buildCommands(): Promise<void> {
    const networkState = await firstValueFrom(this.networkService.state$);
    const project = this.projectService.currentProjectId;

    if (networkState === 'online') {
      this.cmdProject = this.newCommand('topic', routeProjects, false);
      this.cmdOrders = this.newCommand('article', `${routeOrders}/${project}`, true);
    } else {
      this.cmdProject = undefined;
      this.cmdOrders = undefined;
    }
    this.cmdOfflineOrders =
      (await this.offlineFormsIndexDbClient.count()) === 0
        ? undefined
        : this.newCommand('cloud_off', `${routeOfflineOrders}`, false);

    // this.cmdSearch = this.newCommand('search', `${routeSearch}/${project}`, true);
    /*

    this.cmdLoops = this.newCommand('home', `${routeBasicNavigation}/${wd}`, true);
    this.cmdCustomNavigation = this.newCommand('format_align_justify', `${routeCustomNavigation}/${wd}`, true);
    this.cmdInfoChanged = this.newCommand('published_with_changes', `${routeInfoChanged}/${wd}`, true);
    this.cmdInfoHistory = this.newCommand('history_toggle_off', `${routeInfoHistory}/${wd}`, true);
    this.cmdInfoWebDoku = this.newCommand('analytics', `${routeInfoWebDoku}/${wd}`, true);

     */

    if (networkState === 'online') {
      this.cmdSettings = this.newCommand('settings', routeSettings);
      this.cmdSignOff = this.newCommand('power_settings_new', routeLogoff);
      this.cmdUser = this.newCommand('person', 'user'); /* not a real route ! */
    } else {
      this.cmdSettings = undefined;
      this.cmdSignOff = undefined;
      this.cmdUser = undefined;
    }

    const sett = this.settingsService.settings.helpSettings;

    if (sett.defaultHelpFileVisible) {
      this.cmdDefaultHelp = this.newCommand('help', routeManual);
    }

    if (sett.customHelpFileVisible()) {
      this.cmdCustomHelp = new MenuCommand(this.translateService);
      this.cmdCustomHelp.icon = 'info';
      this.cmdCustomHelp.route = sett.customHelpFile;
      this.cmdCustomHelp.customText = sett.customHelpFileText;
      this.cmdCustomHelp.isExternalRoute = true;
    }

    this.rebuilt$.next();
  }

  newCommand(icon: string, route: string, needProject = false): MenuCommand {
    const cmd = new MenuCommand(this.translateService);
    cmd.icon = icon;
    cmd.route = route;
    cmd.transId = this.splitString(route);
    cmd.needProject = needProject;
    return cmd;
  }

  splitString(text: string): string {
    if (RBCheck.isNullOrEmpty(text)) {
      return '';
    }
    const parts = text.split('/');
    if (parts.length <= 1) {
      return text;
    }
    return parts[0];
  }

  public hideNavigation() {
    this.navigationAllowed = false;
  }

  public showNavigation() {
    this.navigationAllowed = true;
  }
}
